import { Card } from "PFComponents/card";
import PageTitle from "PFComponents/page/page_title";
import AutoSelect from "PFComponents/select/autoselect";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { fetchAutocompleteSkills } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { WORKFLOW_STATES } from "../../../../constants/workflow_states";
import { fetchProfilesCount, fetchRolesCount } from "../../../services/api";
import { CountItem } from "./count_item";
import css from "./skills_utilization.module.scss";

export type Skill = {
  id: number;
  global_id: string;
  ordinal: any;
  text: string;
  type: string;
};

export const SkillsUtilization = (): JSX.Element => {
  const { data: currentProfile } = useCurrentProfile();
  const { t } = useTranslation("insights");
  const roleTemplate =
    currentProfile.templates && currentProfile.templates.find((item) => item.name === "role");

  const [skill, setSkill] = useState<Skill | null>(null);
  const [rolesCount, setRolesCount] = useState<number | null>(null);
  const [profilesCount, setProfilesCount] = useState<number | null>(null);

  const handleFetchProfileCount = () => {
    skill && fetchProfilesCount(skill).then((resp) => setProfilesCount(resp.meta.total));
  };

  const handleFetchRolesCount = () => {
    skill && fetchRolesCount(roleTemplate, skill).then((resp) => setRolesCount(resp.meta.total));
  };

  useEffect(() => {
    if (skill) {
      handleFetchProfileCount();
      handleFetchRolesCount();
    } else {
      setRolesCount(0);
      setProfilesCount(0);
    }
  }, [skill]);

  return (
    <Card className={css.root} shadowVariant="none">
      <div className={css.calendarColumn}>
        <PageTitle qaId="Insights.title" subtitle>
          {t("skillsDemandAndDistribution")}
        </PageTitle>

        <div className={css.spacing}>{t("selectASkill")}</div>
        <AutoSelect
          label={t("skill")}
          letCreate={false}
          letClear
          closeOnChange
          multi={false}
          query={(term: string) => fetchAutocompleteSkills({ term, global: false, value: term })}
          formatOption={(item) => ({
            id: item.id,
            displayElement: item.text,
            item: item
          })}
          values={skill ? [{ ...skill, text: skill.text }] : null}
          handleChange={(selections) => setSkill(selections[0])}
          style={{ padding: 0, marginBottom: 20, maxWidth: 400 }}
        />
        <CountItem
          count={rolesCount}
          skill={skill}
          countLabelKey="countActiveRoles"
          foundItemLabelKey="haveSkillRequirement"
          linkLabelKey="viewYours"
          linkTo={{
            pathname: `/workflow/roles`,
            state: {
              filters: {
                strings: {
                  skills: [skill?.id]
                },
                fields: {
                  state: ["new"],
                  workflow_state: Object.keys(WORKFLOW_STATES)
                }
              }
            }
          }}
        />
        <CountItem
          count={profilesCount}
          skill={skill}
          countLabelKey="countPeople"
          foundItemLabelKey="haveSkillOnProfile"
          linkLabelKey="viewAll"
          linkTo={{
            pathname: "/profiles",
            state: {
              filters: {
                strings: {
                  skills: [skill?.id]
                }
              }
            }
          }}
        />
      </div>
    </Card>
  );
};
